import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useRoomSession, useRoomSessionDetections } from '../../../context/room_session';
import { AppText, EditableNote, styled } from '@streem/ui-react';
import { useObservable } from '../../../hooks/rxjs';
import { StreemshotId } from '@streem/domain-id';
import { ModelSerialFeedback } from './model_serial_list/model_serial_feedback';
import { useCompanySettings } from '../../../context/company_settings';
import { TimeStampController } from '../../../util/timestamp_controller';
export const InCallStreemshots = () => {
    const roomSession = useRoomSession();
    const [streemshots] = useObservable(roomSession.streemshots.responses);
    const roomSessionDetections = useRoomSessionDetections();
    const companySettings = useCompanySettings();
    const [recordings = []] = useObservable(roomSession.recording.recordings);
    const timeStampController = new TimeStampController(recordings);
    return (_jsx(StreemshotListWrapper, { children: streemshots &&
            streemshots.map((streemshot, index) => {
                const detectionsForStreemshot = roomSessionDetections.filter(detection => {
                    // See comment on revisions in model_serial_list.tsx, near here:
                    //      https://github.com/streem/streem-app/blob/main/web/packages/sdk-react/src/components/in_call/spectator/model_serial_list/model_serial_list.tsx#L37
                    return (detection.streemshotSid === streemshot.streemshotId ||
                        detection.streemshotSid === streemshot.id);
                });
                return (_jsx(Streemshot, { id: new StreemshotId(streemshot?.streemshotId || streemshot.id), streemshot: streemshot, detections: detectionsForStreemshot, dataTestId: 'streemshot ' + index.toString(), noteCharacterLimit: companySettings?.streemshotNoteCharLimit, timeStampController: timeStampController }, streemshot.id));
            }) }));
};
function Streemshot({ id, streemshot, dataTestId, detections, noteCharacterLimit = 225, timeStampController, }) {
    const roomSession = useRoomSession();
    const [streemshotNote] = useObservable(roomSession.note.getStreemshotNote(id));
    const [errorMessage, setErrorMessage] = useState('');
    const timeStamp = timeStampController.getStreemShotTimeStamp(streemshot);
    if (!streemshot.downloadUrl) {
        return _jsx(_Fragment, {});
    }
    return (_jsxs(StreemshotWithNotesWrapper, { children: [_jsx(StreemshotImage, { onClick: () => {
                    window.open(streemshot.downloadUrl, '_blank', 'noopener,noreferrer');
                }, src: streemshot.downloadUrl, "data-testid": dataTestId, "data-clarity-mask": "true" }), _jsx(TimeStampWrapper, { children: _jsx(AppText, { size: "large", color: "white", style: { fontSize: '1rem' }, children: timeStamp }) }), _jsx(ModelSerialWrapper, { children: _jsx(ModelSerialFeedback, { modelSerialDetections: detections, onAdd: (description, type) => {
                        roomSession.detections.createDetection(id, description, type);
                    }, onEdit: (detectionId, newDetection) => {
                        if (typeof newDetection?.description === 'string' && newDetection.type) {
                            roomSession.detections.updateDetection(detectionId, newDetection?.description, newDetection?.type);
                        }
                    }, onDelete: detectionId => {
                        roomSession.detections.deleteDetection(detectionId);
                    } }) }), _jsx(EditableNote, { testId: "artifact-notes", hasFootBar: false, description: "", placeholderText: 'Add notes', characterLimit: noteCharacterLimit, noteText: streemshotNote?.text ?? '', errorMessage: errorMessage, onSave: async (noteText) => {
                    try {
                        setErrorMessage('');
                        await roomSession.note.saveStreemshotNote(id, noteText);
                    }
                    catch (error) {
                        setErrorMessage('Error attempting to edit notes.');
                    }
                } })] }, `streemshot-${streemshot.downloadUrl}`));
}
const StreemshotListWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginTop: '16px',
    marginBottom: '16px',
});
const ModelSerialWrapper = styled.div({
    marginTop: '16px',
    paddingBottom: '12px',
});
const StreemshotWithNotesWrapper = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing.l,
    position: 'relative',
}));
const StreemshotImage = styled.img(({ theme }) => ({
    // TODO: Use css aspect-ratio once widely supported
    // https://caniuse.com/mdn-css_properties_aspect-ratio
    height: 227.25,
    marginBottom: theme.spacing.s,
    backgroundColor: theme.colors.grey90,
    objectFit: 'contain',
    cursor: 'pointer',
    border: `1px solid ${theme.colors.grey20}`,
    '&:hover': {
        filter: 'brightness(0.9)',
    },
}));
const TimeStampWrapper = styled.div(({ theme }) => ({
    position: 'absolute',
    top: '.5rem',
    right: '8.875rem',
    padding: '.125rem .5rem .375rem .5rem',
    borderRadius: '.25rem',
    background: 'none',
    backgroundColor: theme.colors.blackA40,
}));
//# sourceMappingURL=in_call_streemshots.js.map